<template>
    <div
        v-if="notifications.length > 0"
        ref="notificationCards">
        <div
            v-if="label"
            class="nav-title">
            {{ label }}
        </div>
        <div
            v-for="notification in notifications"
            :key="notification.id">
            <comment-notification-card
                v-if="isCommentNotification(notification)"
                v-bind="getCommonProps(notification)"
                @card-clicked="onCardClicked" />
            <request-notification-card
                v-else-if="getNotificationType(notification) === 'accessRequest'"
                v-bind="getCommonProps(notification)"
                :request-status="notification.notification.messageMetadata.requestStatus"
                :requested-permission="notification.notification.messageMetadata.requestAction"
                :requested-entity-type="notification.notification.messageMetadata.requestEntityType"
                :requested-entity-id="notification.notification.messageMetadata.requestEntityId"
                :request-id="notification.notification.messageMetadata.requestId"
                @manage-access="onManageAccess"
                @card-clicked="onCardClicked" />
            <bulk-notification-card
                v-else-if="isBulkNotification(notification)"
                v-bind="getCommonProps(notification)"
                @card-clicked="onCardClicked" />
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import CommentNotificationCard from './comment-notification-card.vue';
import RequestNotificationCard from './request-notification-card.vue';
import BulkNotificationCard from './bulk-notification-card.vue';
import { isBulkNotification } from './notifications.utils';

export default {
    name: 'NotificationList',
    components: {
        CommentNotificationCard,
        RequestNotificationCard,
        BulkNotificationCard,
    },
    props: {
        notifications: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: '',
        },
    },
    emits: ['cardClicked', 'manage-access'],
    setup () {
        const notificationCards = ref(null);

        return {
            notificationCards,
        };
    },
    methods: {
        isBulkNotification,
        isCommentNotification (notification) {
            return this.getNotificationType(notification) === 'comment' || this.getNotificationType(notification) === 'InAppCommentThread';
        },
        getNotificationType (notification) {
            if(notification.type) {
                return notification.type;
            }

            if (notification.notificationSource === 'comment') {
                return 'comment';
            }

            return undefined;
        },
        getCommonProps (notification) {
            if(notification.type === 'InAppCommentThread') {
                return this.formatInAppComment(notification);
            }

            const data = notification.notification;

            if (isBulkNotification(notification)) {
                return {
                    id: notification.id,
                    header: data.headlineMessage,
                    body: data.body,
                    timeStamp: notification.formattedTimestamp,
                    unread: notification.isUnread,
                };
            }

            const user = {
                fullName: data.actionUser.name,
                username: data.actionUser.email,
            };

            return {
                id: notification.id,
                user,
                header: data.headlineMessage,
                body: data.body,
                timeStamp: notification.formattedTimestamp,
                unread: notification.isUnread,
            };
        },
        formatInAppComment (notification) {
            return {
                ...notification,
                header: `New message from ${notification.user.fullName}`,
                body: notification.notification.comment,
                timeStamp: notification.formattedTimestamp,
                unread: !notification.read,
                routeObj: this.generateRouteObjectForInAppComment(notification),
            };
        },
        generateRouteObjectForInAppComment (notification) {
            if(!notification?.notification?.threadEntities) return null;

            const { threadEntities } = notification.notification;
            const threadId = notification.entityId;

            if(threadEntities.dashboard) {
                return {
                    name: 'Dashboard',
                    params: {
                        id: threadEntities.dashboard[0],
                    },
                    query: {
                        widgetId: threadEntities.widget?.[0],
                        openThread: true,
                        threadId,
                    },
                };
            }

            if(threadEntities.replay) {
                return {
                    name: 'SessionReplayRecording',
                    params: {
                        recordingSessionId: threadEntities.replay[0],
                    },
                    query: {
                        threadId,
                    },
                };
            }

            if(threadEntities['saved clip']) {
                return {
                    name: 'SavedClipRecording',
                    params: {
                        id: threadEntities['saved clip'][0],
                    },
                    query: {
                        threadId,
                    },
                };
            }

            if(threadEntities.idea) {
                return {
                    name: 'Validate',
                    query: {
                        openItem: threadEntities.idea[0],
                        threadId,
                    },
                };
            }

            if(threadEntities.feedback) {
                return {
                    name: 'ListenFeedbackAllFeedbackView',
                    query: {
                        openItem: threadEntities.feedback[0],
                        threadId,
                    },
                };
            }

            return null;
        },
        onCardClicked (id) {
            this.$emit('cardClicked', id);
        },
        onManageAccess (data) {
            this.$emit('manage-access', data);
        },
    },
};
</script>

<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.nav-title {
    margin-top: 20px;
    margin-bottom: 8px;
}
</style>
